import { useApolloClient } from "@apollo/client";
import React, { useCallback, useMemo } from "react";

import useBurstQuery from "modules/hooks/use-burst-query";
import { TYPING_TIMEOUT_MILLISECONDS } from "modules/utils/Constants";

import { Button, HStack, IconButton } from "@chakra-ui/react";
import colors from "app/colors";
import {
  JobQuery,
  JobQueryVariables,
  JobStatusType,
  JobType,
  MessageFragment,
  MessageQuery,
  MessageQueryVariables,
  MessageThreadsQuery,
  MessageThreadsQueryVariables,
  UserListQuery,
  UserStatusType,
} from "gql/graphql";
import COUNT_QUERY from "modules/hooks/_networking/count";
import useDisplayToast from "modules/hooks/use-display-toast";
import useJobCount from "modules/hooks/use-job-count";
import useMessageThreadCache from "modules/hooks/use-message-thread-cache";
import useQueryString, {
  MESSAGE_THREAD_ID_QUERY_STRING,
} from "modules/hooks/use-query-string";
import { CircleOutlineCheckIcon, CloseIcon } from "modules/icons";
import MESSAGE_QUERY from "modules/messaging/_networking/message";
import MESSAGE_FRAGMENT from "modules/messaging/_networking/message-fragment";
import MESSAGE_THREADS_QUERY from "modules/messaging/_networking/message-threads";
import ACTIVE_CALL_LIST_QUERY from "modules/messaging/left-nav/networking/active-call-list-query";
import JOB_QUERY from "modules/modals/networking/job";
import CALL_BY_ID_QUERY from "modules/providers/video-call-provider/networking/call-query";
import Toast from "modules/toasts/toast";
import memoizeDebounce from "modules/utils/memoized-debounce";
import { useRouter } from "next/router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useAuth } from "../auth-provider";
import { useDistributionGroups } from "../distribution-groups-provider";
import { useNotification } from "../notification-provider/notification-provider";
import { useOrganization } from "../organization-provider";
import { useOverlay } from "../overlay-provider";
import { DRAWER_TYPE, MODAL_TYPE } from "../overlay-provider/overlay-provider";
import { usePubSub } from "../pub-sub-provider";
import { useTags } from "../tags-provider";
import { useTyping } from "../typing-provider";
import { useUserAttributes } from "../user-attributes-provider";
import { useUserPresence } from "../user-presence-provider";
import { useUsers } from "../users-provider";
import USER_LIST_QUERY from "../users-provider/networking/user-list";
import type { IWebSocketPayloadIncomingCall } from "./types";
import {
  IWebSocketPayloadAlert,
  IWebSocketPayloadBase,
  IWebSocketPayloadCall,
  IWebSocketPayloadCallEnded,
  IWebSocketPayloadDistributionGroup,
  IWebSocketPayloadMessage,
  IWebSocketPayloadMessageSeen,
  IWebSocketPayloadMessageThread,
  IWebSocketPayloadNewMessage,
  IWebSocketPayloadTag,
  IWebSocketPayloadTagCategory,
  IWebSocketPayloadTyping,
  IWebSocketPayloadUser,
  IWebSocketPayloadUserAttribute,
  IWebSocketPayloadUserAttributeCategory,
  IWebSocketPayloadUsersOnline,
  WebSocketMessageType,
  WebSocketNotificationType,
} from "./types";

/** A ring older than this (by payload `createTime`) is considered stale and is not shown. */
const STALE_INCOMING_CALL_MILLISECONDS = 60_000;

const useHandleIncomingMessage = () => {
  const { videoChat: videoChatFeatureFlag } = useFlags();
  const { me } = useAuth();
  const apolloClient = useApolloClient();
  const { displayToast } = useDisplayToast();
  const notification = useNotification();
  const [messageThreadsAPI] = useBurstQuery<
    MessageThreadsQuery,
    MessageThreadsQueryVariables
  >(MESSAGE_THREADS_QUERY);
  const [messageAPI] = useBurstQuery<MessageQuery, MessageQueryVariables>(
    MESSAGE_QUERY,
  );
  const { refreshUserAttributes, refreshUserAttributeCategories } =
    useUserAttributes();
  const { refreshTags, refreshTagCategory } = useTags();
  const { refreshDistributionGroups } = useDistributionGroups();
  const { refreshOrganization } = useOrganization();
  const { refreshUsers } = useUsers();
  const { updateStartedTyping, updateStoppedTyping } = useTyping();
  const { incrementUnseenJobCount } = useJobCount();
  const { updateMessageThreadQueryCache } = useMessageThreadCache();
  const { publish } = usePubSub();
  const { setUsersOnlineStatus } = useUserPresence();
  const { openDrawer, openModal, closeModals } = useOverlay();
  const { qsMessageThreadId } = useQueryString();
  const { query, push } = useRouter();

  const [jobAPI] = useBurstQuery<JobQuery, JobQueryVariables>(JOB_QUERY);

  const updateStoppedTypingAfterDelay = useMemo(
    () =>
      memoizeDebounce(
        (messageThreadId: string, userId: string) => {
          updateStoppedTyping(messageThreadId, userId);
        },
        TYPING_TIMEOUT_MILLISECONDS,
        {},
        (messageThreadId, userId) => `${messageThreadId}-${userId}`,
      ),
    [updateStoppedTyping],
  );

  const getCachedMessageIds = useCallback(
    (messageThreadId: string) => {
      const messageIds: string[] = [];
      const cacheData: Record<string, any> = apolloClient.cache.extract();
      for (const cacheKey in cacheData) {
        const entity = cacheData[cacheKey];

        // Check if this entity is a Message and has matching messageThreadId
        if (
          entity.__typename === "Message" &&
          entity.messageThreadId === messageThreadId
        ) {
          // cacheKey is usually "Message:<id>"
          const id = cacheKey.split(":")[1];
          if (id) {
            messageIds.push(id);
          }
        }
      }

      return messageIds;
    },
    [apolloClient.cache],
  );

  const handleIncomingMessage = useCallback(
    async (data: IWebSocketPayloadBase) => {
      try {
        //drop BADGE notifications
        switch (data.notificationType) {
          case WebSocketNotificationType.ALERT: {
            const {
              messageBody,
              messageThreadId,
              messageTitle,
              createdUserName,
            } = data as any as IWebSocketPayloadAlert;

            const firstName = (createdUserName || "").split(" ")[0];

            notification.displayNewMessageNotification({
              title: messageTitle,
              body: messageBody ? `${firstName}: ${messageBody}` : undefined,
              messageThreadId,
            });
            break;
          }
          case WebSocketNotificationType.DATA: {
            switch (data.type) {
              case WebSocketMessageType.PONG:
                // no-op: handled by WebSocketClient
                break;

              case WebSocketMessageType.NEW_MESSAGE: {
                const { id, parentId } =
                  data as any as IWebSocketPayloadNewMessage;

                const [messageData, messageThreadData] = await Promise.all([
                  messageAPI({ variables: { id: [id] } }),
                  messageThreadsAPI({
                    variables: { id: [parentId] },
                  }),
                ]);

                const newMessage =
                  messageData.data.messages.length === 1
                    ? messageData.data.messages[0]
                    : undefined;

                const messageThread =
                  messageThreadData.data.messageThreads.length === 1
                    ? messageThreadData.data.messageThreads[0]
                    : undefined;

                if (!newMessage || !messageThread) {
                  break;
                }

                publish("new-message", newMessage);
                updateStoppedTyping(messageThread.id, newMessage.createdUserId);

                updateMessageThreadQueryCache(messageThread);

                apolloClient.refetchQueries({
                  include: [COUNT_QUERY],
                });

                break;
              }

              case WebSocketMessageType.MESSAGE_THREAD: {
                const { id, idList } =
                  data as any as IWebSocketPayloadMessageThread;

                const _ids = id ? [id] : idList;

                if (!_ids || _ids.length === 0) {
                  return;
                }

                const messageThreadsData = await messageThreadsAPI({
                  variables: { id: _ids },
                }); // automatically updates the data in the cache
                const messageThreads =
                  messageThreadsData.data?.messageThreads ?? [];

                apolloClient.refetchQueries({
                  include: [COUNT_QUERY],
                });

                messageThreads.forEach((messageThread) => {
                  if (messageThread.isDeleted) {
                    apolloClient.cache.evict({
                      id: apolloClient.cache.identify({
                        __typename: "MessageThread",
                        id: messageThread.id,
                      }),
                    });

                    getCachedMessageIds(messageThread.id).forEach(
                      (messageId) => {
                        apolloClient.cache.evict({
                          id: apolloClient.cache.identify({
                            __typename: "Message",
                            id: messageId,
                          }),
                        });
                      },
                    );

                    apolloClient.cache.gc();

                    //If the user happens to be viewing the deleted message thread, redirect them away
                    if (messageThread.id === qsMessageThreadId) {
                      const { [MESSAGE_THREAD_ID_QUERY_STRING]: _, ...rest } =
                        query;
                      push({
                        query: rest,
                      });
                    }

                    return;
                  }

                  updateMessageThreadQueryCache(messageThread);
                });

                break;
              }

              case WebSocketMessageType.MESSAGE: {
                const { id, idList } = data as any as IWebSocketPayloadMessage;

                const _ids = id ? [id] : idList;

                if (!_ids || _ids.length === 0) {
                  break;
                }

                const messageData = await messageAPI({
                  variables: { id: _ids },
                });
                // messages may no longer exist (deleted) or not be visible
                // to this user; there is nothing to publish then
                const messages = messageData.data?.messages ?? [];
                messages.forEach((message) => {
                  publish("message-updated", message);
                });
                break;
              }

              case WebSocketMessageType.USER_ATTRIBUTE_CATEGORY: {
                const { id } =
                  data as any as IWebSocketPayloadUserAttributeCategory;
                if (id) refreshUserAttributeCategories([id]);
                break;
              }

              case WebSocketMessageType.USER_ATTRIBUTE: {
                const { id, idList } =
                  data as any as IWebSocketPayloadUserAttribute;
                if (id) refreshUserAttributes([id]);
                if (idList) refreshUserAttributes(idList);
                break;
              }

              case WebSocketMessageType.TAG_CATEGORY: {
                const { id } = data as any as IWebSocketPayloadTagCategory;
                refreshTagCategory(id);
                break;
              }

              case WebSocketMessageType.TAG: {
                const { id, idList } = data as any as IWebSocketPayloadTag;
                if (id) refreshTags([id]);
                if (idList) refreshTags(idList);
                break;
              }

              case WebSocketMessageType.DISTRIBUTION_GROUP: {
                const { id, idList } =
                  data as any as IWebSocketPayloadDistributionGroup;
                if (id) refreshDistributionGroups([id]);
                if (idList) refreshDistributionGroups(idList);
                break;
              }

              case WebSocketMessageType.USER: {
                const { id, idList } = data as any as IWebSocketPayloadUser;
                if (id) refreshUsers([id]);
                if (idList) refreshUsers(idList);
                break;
              }

              case WebSocketMessageType.ORGANIZATION: {
                refreshOrganization();
                break;
              }

              case WebSocketMessageType.JOB: {
                const { id } = data as any as IWebSocketPayloadUser;
                if (id) {
                  const response = await jobAPI({
                    variables: { id },
                  });
                  if (response.data) {
                    const job = response.data.job;
                    if (
                      job &&
                      job.active &&
                      job.status === JobStatusType.Complete
                    ) {
                      incrementUnseenJobCount();
                      switch (job.type) {
                        case JobType.MessageThreadPdf: {
                          displayToast(({ onClose }) => (
                            <Toast
                              message="New file available for download/print"
                              leftIcon={
                                <CircleOutlineCheckIcon
                                  boxSize={6}
                                  stroke={colors.grayMain}
                                />
                              }
                              rightElement={
                                <HStack>
                                  <Button
                                    variant="outline"
                                    size="xs"
                                    color={colors.white}
                                    fontWeight={700}
                                    onClick={() => {
                                      openDrawer({
                                        type: DRAWER_TYPE.DOWNLOADS_EXPORTS,
                                      });
                                      onClose();
                                    }}
                                  >
                                    My Downloads
                                  </Button>
                                  <IconButton
                                    variant="ghost"
                                    aria-label="Close"
                                    icon={
                                      <CloseIcon
                                        fill={colors.gray70}
                                        boxSize={5}
                                      />
                                    }
                                    size="sm"
                                    onClick={onClose}
                                  />
                                </HStack>
                              }
                            />
                          ));
                          break;
                        }
                        default:
                          break;
                      }
                    }
                  }
                }
                break;
              }

              case WebSocketMessageType.CALL: {
                const { id } = data as unknown as IWebSocketPayloadCall;
                if (id) {
                  await Promise.all([
                    apolloClient.query({
                      query: CALL_BY_ID_QUERY,
                      variables: { id },
                      fetchPolicy: "network-only",
                    }),
                    apolloClient.query({
                      query: ACTIVE_CALL_LIST_QUERY,
                      variables: { input: { pageSize: 50 } },
                      fetchPolicy: "network-only",
                    }),
                  ]);
                }
                break;
              }

              case WebSocketMessageType.USERS_ONLINE: {
                const { statusType, userIds }: IWebSocketPayloadUsersOnline =
                  data as any;
                if (statusType === UserStatusType.Offline) {
                  const checkInTime = new Date().toISOString();

                  // Doing as a batch update instead of calling modify multiple times in hopes to reduce the number of observer changes in components
                  apolloClient.cache.updateQuery<UserListQuery>(
                    { query: USER_LIST_QUERY },
                    (data) => {
                      if (!data) {
                        return data;
                      }

                      return {
                        ...data,
                        userList: data.userList.map((user) => {
                          if (!userIds.includes(user.id)) {
                            return user;
                          }

                          return {
                            ...user,
                            checkInTime,
                          };
                        }),
                      };
                    },
                  );
                }
                setUsersOnlineStatus(userIds, statusType);

                break;
              }

              case WebSocketMessageType.TYPING: {
                const { messageThreadId, userId } =
                  data as any as IWebSocketPayloadTyping;

                updateStartedTyping(messageThreadId, userId);
                updateStoppedTypingAfterDelay(messageThreadId, userId);

                break;
              }

              case WebSocketMessageType.MESSAGE_SEEN: {
                const { messageId, seenUserIdList } =
                  data as any as IWebSocketPayloadMessageSeen;

                apolloClient.cache.modify({
                  id: `Message:${messageId}`,
                  fields: {
                    seenUserIdList() {
                      return seenUserIdList;
                    },
                    isSeen(existing: boolean) {
                      if (me && seenUserIdList.includes(me.id)) {
                        return true;
                      }
                      return existing;
                    },
                  },
                });

                const message =
                  apolloClient.cache.readFragment<MessageFragment>({
                    id: `Message:${messageId}`,
                    fragment: MESSAGE_FRAGMENT,
                    fragmentName: "Message",
                  });

                if (message) {
                  publish("message-updated", message);
                }

                break;
              }

              case WebSocketMessageType.INCOMING_CALL: {
                if (!videoChatFeatureFlag) break;
                const d = data as unknown as Record<string, unknown>;
                const callId =
                  typeof d.callId === "string" && d.callId ? d.callId : "";
                if (!callId) break;
                // A delayed delivery (e.g. reconnect replay) should not ring
                // long after the call started.
                const createdAtMilliseconds = Date.parse(
                  String(d.createTime ?? ""),
                );
                if (
                  Number.isFinite(createdAtMilliseconds) &&
                  Date.now() - createdAtMilliseconds >
                    STALE_INCOMING_CALL_MILLISECONDS
                ) {
                  break;
                }
                const rawList = d.accessUserIdList;
                const accessUserIdList = Array.isArray(rawList)
                  ? rawList.map((id) => String(id))
                  : [];
                const fromUserId = String(d.fromUserId ?? "");
                const createdUserId = String(
                  d.createdUserId ?? d.fromUserId ?? "",
                );
                const call: IWebSocketPayloadIncomingCall = {
                  fromUserId,
                  createdUserId,
                  accessUserIdList,
                  isVideo: Boolean(d.isVideo),
                  createTime: String(d.createTime ?? ""),
                  host: String(d.host ?? ""),
                  callId,
                  token: String(d.token ?? ""),
                };
                openModal({
                  type: MODAL_TYPE.INCOMING_CALL,
                  call,
                });

                break;
              }

              case WebSocketMessageType.CALL_ENDED: {
                const { callId } =
                  data as unknown as IWebSocketPayloadCallEnded;
                if (!callId) break;

                // Stop ringing for this call — an active call session (the
                // user already joined) is intentionally left untouched.
                closeModals({
                  predicate: (modal) =>
                    modal.type === MODAL_TYPE.INCOMING_CALL &&
                    modal.call?.callId === callId,
                });

                await apolloClient.query({
                  query: ACTIVE_CALL_LIST_QUERY,
                  variables: { input: { pageSize: 50 } },
                  fetchPolicy: "network-only",
                });

                break;
              }

              case WebSocketMessageType.REMOVE_NOTIFICATION: {
                //drop
                break;
              }

              default: {
                // eslint-disable-next-line no-console
                console.log("Received unhandled websocket notification", data);
                break;
              }
            }
            break;
          }
          default: {
            break;
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error processing websocket event", error);
      }
    },
    [
      setUsersOnlineStatus,
      notification,
      messageAPI,
      messageThreadsAPI,
      publish,
      updateStoppedTyping,
      updateMessageThreadQueryCache,
      apolloClient,
      getCachedMessageIds,
      qsMessageThreadId,
      query,
      push,
      refreshUserAttributeCategories,
      refreshUserAttributes,
      refreshTagCategory,
      refreshTags,
      refreshDistributionGroups,
      refreshUsers,
      refreshOrganization,
      jobAPI,
      incrementUnseenJobCount,
      displayToast,
      openDrawer,
      updateStartedTyping,
      updateStoppedTypingAfterDelay,
      me,
      openModal,
      closeModals,
      videoChatFeatureFlag,
    ],
  );

  return handleIncomingMessage;
};

export default useHandleIncomingMessage;
